import { CreateLinkDocument, DeleteLinkDocument, UpdateLinkDocument } from '@/generated/graphql';
import { useApolloClient, useMutation } from '@vue/apollo-composable';
import { Ref } from 'vue';
import { emptyLink } from '../multilink';
import { FormLink, Link } from '../types';

type CreateLinkParams = {
    multilinkId: string;
    name: string;
    to: string;
    description?: string;
};

type UpdateLinkParams = {
    id: string;
    name?: string;
    to?: string;
    description?: string;
};

type DeleteLinkParams = {
    id: string;
};

export function useMultiLink(createMultiLink: (fieldName: string) => Promise<string | undefined>) {
    const deleteLink = useMutation(DeleteLinkDocument);
    const createLink = useMutation(CreateLinkDocument);
    const updateLink = useMutation(UpdateLinkDocument);
    const { client } = useApolloClient();

    async function createLinks(linksToCreate: CreateLinkParams[]) {
        for (const link of linksToCreate) {
            await createLink.mutate({
                multiLinkId: link.multilinkId,
                name: link.name.trim(),
                to: link.to.trim(),
                description: link.description?.trim(),
            });
        }
    }

    async function updateLinks(linksToUpdate: UpdateLinkParams[]) {
        for (const link of linksToUpdate) {
            await updateLink.mutate({
                id: link.id,
                name: link.name?.trim(),
                to: link.to?.trim(),
                description: link.description?.trim(),
            });
        }
    }

    async function deleteLinks(linksToDelete: DeleteLinkParams[]) {
        for (const link of linksToDelete) {
            await deleteLink.mutate({ id: link.id });
        }
    }

    function hasMultiLinkChanges(links: FormLink[]) {
        if (links.length > 0) {
            return links.some((l) => l.status !== 'skip');
        }
        return false;
    }

    async function saveMultiLinks(multilinkId: string, links: FormLink[]) {
        const linksToCreate = links
            .filter((l) => l.status === 'create' && l.name.length > 0 && l.to.length > 0)
            .map((l) => ({ ...l, multilinkId }));
        const linksToDelete = links
            .filter((l) => l.status === 'delete' && !l.id.startsWith('new'))
            .map((l) => ({ id: l.id }));
        const linksToUpdate = links
            .filter((l) => l.status === 'update')
            .map((l) => ({ id: l.id, name: l.name, to: l.to, description: l.description }));

        await createLinks(linksToCreate);
        await deleteLinks(linksToDelete);
        await updateLinks(linksToUpdate);

        client.cache.evict({
            id: client.cache.identify({ __typename: 'Multilink', id: multilinkId }),
            fieldName: 'links',
        });
        client.cache.gc();
    }

    function getFormFieldLinks(links: Ref<Link[]>) {
        return [
            ...links.value.map((link) => ({
                ...link,
                status: 'skip' as const,
            })),
            emptyLink(),
        ];
    }

    async function saveMultiLinkFields(fields: { id: string | undefined; fieldName: string; links: FormLink[] }[]) {
        for (const field of fields) {
            if (hasMultiLinkChanges(field.links)) {
                const multiLinkId = field.id ?? (await createMultiLink(field.fieldName));
                if (multiLinkId) {
                    await saveMultiLinks(multiLinkId, field.links);
                }
            }
        }
    }

    return {
        hasMultiLinkChanges,
        saveMultiLinks,
        getFormFieldLinks,
        saveMultiLinkFields,
    };
}
